// src/pages/approval/Docs.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  X,
  RotateCcw,
} from "lucide-react";
import axios from "../../api/axios";

// 상태 스타일
const statusStyle = (s) => {
  const styles = {
    approved: "bg-emerald-50 text-emerald-700",
    rejected: "bg-red-50 text-red-700",
    cancel: "bg-gray-100 text-gray-600",
    in_progress: "bg-blue-50 text-blue-700",
    submit: "bg-amber-50 text-amber-700",
  };
  return styles[s] || "bg-gray-100 text-gray-600";
};

const statusText = (s) =>
  ({
    submit: "상신",
    in_progress: "진행중",
    approved: "승인",
    rejected: "반려",
    cancel: "취소",
  }[s] ||
  s ||
  "-");

const moduleText = (s) =>
  ({
    approval: "결재",
    material: "자재",
    safety: "안전",
    quality: "품질",
    design: "설계",
    construction: "시공",
    const: "시공",
  }[s] ||
  s ||
  "-");

// 시공(const) 모듈의 doc_type을 실제 라우트 경로로 매핑
const getConstRoutePath = (docType) => {
  const typeMap = {
    DAILY: "daily",
    DA: "daily",
    WO: "order",
    WORKORDER: "order",
    OR: "ordercomplete",
    ORDERCOMPLETE: "ordercomplete",
    PROGRESS: "progress",
    PR: "progress",
    PHOTO: "photo",
    PH: "photo",
    PHOTOBOARD: "photoboard",
    PB: "photoboard",
  };
  return (
    typeMap[(docType || "").toUpperCase()] || docType?.toLowerCase() || "daily"
  );
};

// 모듈과 doc_type에 따른 실제 뷰 경로 생성
const buildViewPath = (projectId, module, docType, dns) => {
  let m = module || "approval";
  if (m === "construction") m = "const";

  // 시공 모듈은 doc_type을 라우트 경로로 변환
  if (m === "const") {
    const routePath = getConstRoutePath(docType);
    return `/project/${projectId}/const/${routePath}/${dns}/view`;
  }

  // 자재 모듈: material → materials (복수형)
  if (m === "material") {
    const type = (docType || "ps").toLowerCase();
    return `/project/${projectId}/materials/${type}/${dns}/view`;
  }

  // 설계 모듈: doc_type 소문자 변환 (FCR → fcr)
  if (m === "design") {
    const type = (docType || "fcr").toLowerCase();
    return `/project/${projectId}/design/${type}/${dns}/view`;
  }

  // 다른 모듈은 기존대로 (quality, safety, approval 등)
  return `/project/${projectId}/${m}/${docType || "APP"}/${dns}/view`;
};

// 응답 정규화
function normalizeListResponse(data) {
  if (data && Array.isArray(data.results)) {
    return { items: data.results, count: data.count ?? data.results.length };
  }
  if (Array.isArray(data)) {
    return { items: data, count: data.length };
  }
  return { items: [], count: 0 };
}

export default function Docs() {
  const navigate = useNavigate();
  const { id: projectId } = useParams();

  const project = projectId || localStorage.getItem("projectId") || "";

  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [total, setTotal] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // 필터/검색 상태
  const [statusFilter, setStatusFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("title");

  // 목록 로드
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      if (!project) return;
      setLoading(true);
      try {
        const res = await axios.get("approval/alllist/", {
          params: { project, page, page_size: pageSize },
        });
        if (aborted) return;
        const data = normalizeListResponse(res.data);
        setRows(data.items);
        setTotal(data.count);
      } catch (e) {
        if (!aborted) {
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => {
      aborted = true;
    };
  }, [project, page, pageSize]);

  // 필터링
  const filtered = useMemo(() => {
    let list = Array.isArray(rows) ? [...rows] : [];

    // 상태 필터
    if (statusFilter !== "all") {
      list = list.filter(
        (d) => (d?.status || "").toLowerCase() === statusFilter
      );
    }

    // 모듈 필터
    if (moduleFilter !== "all") {
      list = list.filter((d) => {
        const m = (d?.module || "").toLowerCase();
        return (
          m === moduleFilter ||
          (moduleFilter === "const" && m === "construction")
        );
      });
    }

    // 검색 필터
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter((doc) => {
        const title = String(doc.title || "").toLowerCase();
        const authorName = String(doc?.created_by?.name || "").toLowerCase();
        const dns = String(doc.dns || doc.id || "").toLowerCase();

        switch (searchField) {
          case "title":
            return title.includes(q);
          case "author":
            return authorName.includes(q);
          case "dns":
            return dns.includes(q);
          default:
            return (
              title.includes(q) || authorName.includes(q) || dns.includes(q)
            );
        }
      });
    }

    return list;
  }, [rows, statusFilter, moduleFilter, searchTerm, searchField]);

  const totalPages = Math.ceil(total / pageSize) || 1;

  const handleReset = () => {
    setStatusFilter("all");
    setModuleFilter("all");
    setSearchTerm("");
    setSearchField("title");
    setPage(1);
  };

  // 필터가 활성화되었는지 확인
  const hasActiveFilters =
    statusFilter !== "all" ||
    moduleFilter !== "all" ||
    searchTerm ||
    searchField !== "title";

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <main className="mx-auto max-w-lg px-4 pt-4 pb-28 overflow-x-hidden">
        {/* 헤더 */}
        <header className="mb-4">
          <p className="text-xs text-gray-500">전자결재</p>
          <h2 className="text-xl font-bold text-gray-900">전체 결재함</h2>
          <p className="mt-1 text-sm text-gray-500">총 {total}건의 결재 문서</p>
        </header>

        {/* 상태 필터 - 세그먼트 컨트롤 스타일 */}
        <section className="mb-3 overflow-x-auto -mx-4 px-4">
          <div className="inline-flex rounded-xl bg-gray-100 p-1 min-w-max">
            {[
              { key: "all", label: "전체" },
              { key: "in_progress", label: "진행중" },
              { key: "submit", label: "상신" },
              { key: "approved", label: "승인" },
              { key: "rejected", label: "반려" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setStatusFilter(tab.key);
                  setPage(1);
                }}
                className={`rounded-lg px-4 py-2 text-sm font-medium transition-all whitespace-nowrap ${
                  statusFilter === tab.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        {/* 모듈 필터 - 칩 버튼 스타일 */}
        <section className="mb-4">
          <div className="flex flex-wrap gap-2">
            {[
              { key: "all", label: "전체" },
              { key: "approval", label: "결재" },
              { key: "design", label: "설계" },
              { key: "quality", label: "품질" },
              { key: "safety", label: "안전" },
              { key: "material", label: "자재" },
              { key: "const", label: "시공" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setModuleFilter(tab.key);
                  setPage(1);
                }}
                className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-all ${
                  moduleFilter === tab.key
                    ? "bg-blue-500 text-white shadow-sm"
                    : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </section>

        {/* 검색 영역 */}
        <section className="mb-4 space-y-2">
          {/* 검색 입력창 */}
          <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <Search size={18} className="text-gray-400 flex-shrink-0" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="검색어를 입력하세요"
              className="flex-1 min-w-0 bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* 검색 필드 선택 + 초기화 */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <select
                value={searchField}
                onChange={(e) => setSearchField(e.target.value)}
                className="w-full appearance-none rounded-full border border-gray-200 bg-white pl-4 pr-8 py-2.5 text-sm text-gray-700 outline-none shadow-sm focus:border-blue-300"
              >
                <option value="title">제목</option>
                <option value="author">작성자</option>
                <option value="dns">문서번호</option>
                <option value="all">전체 검색</option>
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
            </div>
            <button
              onClick={handleReset}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-sm font-medium shadow-sm transition-colors ${
                hasActiveFilters
                  ? "border-blue-300 bg-blue-50 text-blue-600"
                  : "border-gray-200 bg-white text-gray-500"
              }`}
            >
              <RotateCcw size={14} />
              초기화
            </button>
          </div>
        </section>

        {/* 목록 */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <p className="text-sm text-gray-400">불러오는 중...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-sm text-gray-400">문서가 없습니다.</p>
          </div>
        ) : (
          <section className="space-y-2">
            {filtered.map((row) => {
              const key = row.id || row.dns || row.docs_dns;
              const module = row.module;

              return (
                <button
                  key={key}
                  onClick={() => {
                    const docType = row.doc_type || "APP";
                    const dns = row.dns || row.id || key;
                    navigate(buildViewPath(projectId, module, docType, dns));
                  }}
                  className="w-full rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-1">
                          {row.title}
                        </p>
                        {row.is_urgent && (
                          <span className="flex items-center gap-0.5 rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600">
                            <AlertCircle size={10} />
                            긴급
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500 flex-wrap">
                        <span>{row?.created_by?.name || "작성자 미상"}</span>
                        <span className="text-gray-300">·</span>
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium">
                          {moduleText(row.module)}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span>
                          {row.created_at
                            ? new Date(row.created_at).toLocaleDateString()
                            : "-"}
                        </span>
                      </div>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${statusStyle(
                        row.status
                      )}`}
                    >
                      {statusText(row.status)}
                    </span>
                  </div>
                </button>
              );
            })}
          </section>
        )}

        {/* 페이지네이션 */}
        {!loading && filtered.length > 0 && (
          <footer className="mt-6 flex items-center justify-center gap-4">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 shadow-sm disabled:opacity-40 active:scale-[0.95]"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex items-center gap-1 text-sm">
              <span className="font-semibold text-gray-900">{page}</span>
              <span className="text-gray-400">/</span>
              <span className="text-gray-500">{totalPages}</span>
            </div>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white border border-gray-200 text-gray-600 shadow-sm disabled:opacity-40 active:scale-[0.95]"
            >
              <ChevronRight size={20} />
            </button>
          </footer>
        )}
      </main>

      {/* 하단 액션 버튼 - 전체 너비 */}
      <footer className="fixed inset-x-0 bottom-0 border-t border-gray-200 bg-white/95 backdrop-blur-sm px-4 py-3 safe-area-bottom z-50">
        <div className="mx-auto max-w-lg">
          <button
            onClick={() =>
              navigate(`/project/${projectId}/approval/request/new`)
            }
            className="w-full h-12 rounded-full bg-blue-500 text-sm font-semibold text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg hover:bg-blue-600"
          >
            <Plus size={18} />
            결재 상신
          </button>
        </div>
      </footer>
    </div>
  );
}
